import React from 'react';
import { Box } from '@mui/material';
import { Chip, Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import { LinkOffRounded } from '@mui-symbols-material/w200';
import moment from 'moment';
import { AvTimerRounded, HubRounded, MemoryRounded, SensorsRounded, TrendingUpRounded, VerifiedUserRounded } from '@mui-symbols-material/w300';
import { connectionStatusIconFunc, connectionStatusTextFunc } from '../IoTConnectionWidget/connectionStatus';
import SignalStrengthIcon from '../SignalStrengthIcon';
import { FirmwareInfoSkeleton, NetworkInfoSkeleton, fadeInSx } from './HeroSkeletons';
import { IndustryType } from '../../hooks/useIndustryType';
import { HeroLeadState, showsLiveHeroSummary } from '../../utils/heroLeadState';
import { getSlaChipColors, getSlaStatusHint, getSlaStatusLabel } from '../../config/iotSlaConfig';

export { resolveHeroLeadState, showsLiveHeroSummary } from '../../utils/heroLeadState';
export type { HeroLeadState } from '../../utils/heroLeadState';

interface HeroLeadProps {
    /** Owning client's industry type — semiconductor gets the runtime lead (no IoT). */
    industryType?: IndustryType | null;
    /** Estimated runtime for the semiconductor lead (days installed × average, backend-derived). */
    runtimeHours?: number | null;
    averageRuntimePerDay?: number | null;
    state: HeroLeadState;
    isAdmin: boolean;
    hasIotConnection: boolean;
    compact?: boolean;
    isSystem?: boolean;
    /** Live connection data (present when connected). */
    connectionStatus?: string;
    hubOnline?: boolean;
    networkStandard?: string | null;
    networkProvider?: string | null;
    signalStrength?: number | null;
    /** Network info resolves slower than the rest — holds its slot with a skeleton, collapses if absent. */
    networkInfoLoading?: boolean;
    /** Gateway firmware (device / modem). Slot held by a skeleton while loading, collapses if absent. */
    firmwareVersion?: string | null;
    modemFirmwareVersion?: string | null;
    firmwareLoading?: boolean;
    /** Network scan gate (IBM-1039): the contract is valid but no passed scan applies — the connect CTA gives way to the scan hint. */
    networkScanBlocked?: boolean;
    /** SLA status of the connection (IBM-1045 / IBM-1041, IoT admins only); absent while unknown — the line collapses. */
    sla?: { status: string; level?: string | null } | null;
    /** `insert` of a commissioning submitted within the last 24 h while the SLA is still INACTIVE / EVALUATION_FAILED — swaps the hint for the "submitted" line. */
    commissioningPendingAt?: string | null;
    /** Makes the SLA chip a link (asset: the SLA report under Condition Monitoring). */
    onSlaClick?: () => void;
}

/** Message-style lead (icon + title + subtitle) used for passive IoT states. */
const HeroLeadMessage = ({
    icon,
    title,
    sub,
    iconBox,
    headline,
}: {
    icon: React.ReactNode;
    title: string;
    sub: string;
    iconBox: number;
    headline: 'titleLarge' | 'titleExtraLarge';
}) => {
    const { t } = useTranslation();
    return (
        <>
            <Box sx={{ display: 'flex', width: iconBox, height: iconBox, alignItems: 'center', justifyContent: 'center', '& svg': { fontSize: 40, color: '#bbb' } }}>
                {icon}
            </Box>
            <Box>
                <Typography level="titleSmallLight" sx={{ color: '#666' }}>
                    {t('IoT Connection')}
                </Typography>
                <Typography level={headline} sx={{ color: '#555' }}>
                    {title}
                </Typography>
                <Typography level="bodySmall" sx={{ color: '#777', mt: '4px' }}>
                    {sub}
                </Typography>
            </Box>
        </>
    );
};

/**
 * Adaptive inline lead for the Hero banner. Semiconductor-owned assets have no IoT and
 * lead with runtime; everything else mirrors the IoT state machine
 * (nocontract / valid / connected / expired), with admin-vs-user copy.
 */
const HeroLead = ({
    industryType,
    runtimeHours,
    averageRuntimePerDay,
    state,
    isAdmin,
    hasIotConnection,
    compact,
    isSystem,
    connectionStatus,
    hubOnline,
    networkStandard,
    networkProvider,
    signalStrength,
    networkInfoLoading,
    firmwareVersion,
    modemFirmwareVersion,
    firmwareLoading,
    networkScanBlocked,
    sla,
    commissioningPendingAt,
    onSlaClick,
}: HeroLeadProps) => {
    const { t, i18n } = useTranslation();
    const iconBox = compact ? 48 : 64;
    const headline: 'titleLarge' | 'titleExtraLarge' = compact ? 'titleLarge' : 'titleExtraLarge';

    // Semiconductor (assets): no IoT — runtime lead. Tool > Chamber is shown in the Customer widget.
    if (industryType === IndustryType.SEMICONDUCTOR) {
        // runtimeHours is null only when no installation date is set — fall back to the passive lead.
        const hasRuntime = runtimeHours !== null && runtimeHours !== undefined;
        return (
            <>
                <Box sx={{ display: 'flex', width: iconBox, height: iconBox, alignItems: 'center', justifyContent: 'center', '& svg': { fontSize: compact ? 40 : 52, color: '#333' } }}>
                    <AvTimerRounded />
                </Box>
                <Box>
                    <Typography level="titleSmallLight" sx={{ color: '#666' }}>
                        {t('Runtime')}
                    </Typography>
                    <Typography level={headline} sx={{ color: '#333' }}>
                        {hasRuntime ? `${runtimeHours.toLocaleString(i18n.language)} h` : t('Not available')}
                    </Typography>
                    <Typography level="bodySmall" sx={{ color: '#777', mt: '4px' }}>
                        {hasRuntime && averageRuntimePerDay != null
                            ? t('Average {{hours}}h / day', { hours: (Math.round(averageRuntimePerDay * 10) / 10).toLocaleString(i18n.language) })
                            : t('Since installation')}
                    </Typography>
                </Box>
            </>
        );
    }

    // Connected — or admin with live data still flowing despite an expired/missing
    // contract: live summary with a per-state disclaimer.
    if (showsLiveHeroSummary({ state, isAdmin, hasIotConnection })) {
        const disclaimer =
            state === 'expired'
                ? t("Contract expired — this live data is currently visible only to you (admin). Renew the contract to restore the customer's access.")
                : state === 'nocontract'
                  ? t('No IoT contract — this live data is currently visible only to you (admin). Add a contract to make it available to the customer.')
                  : null;
        return (
            <>
                {/* Same 40px icon metric as HeroLeadMessage so every hero state reads alike. */}
                <Box sx={{ display: 'flex', width: iconBox, height: iconBox, alignItems: 'center', justifyContent: 'center' }}>
                    {connectionStatusIconFunc({ connectionStatus: connectionStatus ?? 'RUNNING', size: 40 })}
                </Box>
                <Box>
                    <Typography level="titleSmallLight" sx={{ color: '#666' }}>
                        {t('IoT Connection')}
                    </Typography>
                    <Typography level={headline} sx={{ color: '#333' }}>
                        {connectionStatusTextFunc({ connectionStatus: connectionStatus ?? 'RUNNING', t, isSystem })}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px', mt: '8px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <HubRounded sx={{ fontSize: 18, color: hubOnline ? '#599460' : '#bbb' }} />
                            <Typography level="bodySmall" sx={{ color: '#666' }}>
                                {t('IoT Hub')} · {hubOnline ? t('Online') : t('Offline')}
                            </Typography>
                        </Box>
                        {networkInfoLoading && !networkStandard && !networkProvider ? (
                            <NetworkInfoSkeleton />
                        ) : (
                            (networkStandard || networkProvider) && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', ...fadeInSx }}>
                                    <SignalStrengthIcon signalStrength={signalStrength} size={20} />
                                    <Typography level="bodySmall" sx={{ color: '#666' }}>
                                        {[networkStandard, networkProvider].filter(Boolean).join(' · ')}
                                    </Typography>
                                </Box>
                            )
                        )}
                    </Box>
                    {sla && (() => {
                        const chip = getSlaChipColors(sla.status, sla.level);
                        const hint = commissioningPendingAt
                            ? t('Commissioning submitted {{date}} · 24h verification starts automatically', { date: moment(commissioningPendingAt).format('L LT') })
                            : getSlaStatusHint(sla.status, sla.level, t);
                        return (
                            <Box
                                data-testid="sla-status"
                                data-sla-status={sla.status}
                                sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', mt: '6px', ...fadeInSx }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <VerifiedUserRounded sx={{ fontSize: 18, color: '#bbb' }} />
                                    <Typography level="bodySmall" sx={{ color: '#666' }}>
                                        {t('SLA status')}
                                    </Typography>
                                </Box>
                                <Chip
                                    size="sm"
                                    variant="soft"
                                    data-testid="sla-status-chip"
                                    onClick={onSlaClick}
                                    sx={{ backgroundColor: chip.bg, color: chip.text, fontWeight: 600 }}
                                >
                                    {getSlaStatusLabel(sla.status, t)}
                                </Chip>
                                {hint && (
                                    <Typography level="bodySmall" sx={{ color: '#666' }} data-testid={commissioningPendingAt ? 'commissioning-pending' : undefined}>
                                        {hint}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })()}
                    {firmwareLoading && !firmwareVersion && !modemFirmwareVersion ? (
                        <Box sx={{ mt: '6px' }}>
                            <FirmwareInfoSkeleton />
                        </Box>
                    ) : (
                        (firmwareVersion || modemFirmwareVersion) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px', mt: '6px', ...fadeInSx }}>
                                {firmwareVersion && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <MemoryRounded sx={{ fontSize: 18, color: '#bbb' }} />
                                        <Typography level="bodySmall" sx={{ color: '#666' }}>
                                            {t('Firmware Version')} · {firmwareVersion}
                                        </Typography>
                                    </Box>
                                )}
                                {modemFirmwareVersion && (
                                    <Typography level="bodySmall" sx={{ color: '#666' }}>
                                        {t('Modem Firmware Version')} · {modemFirmwareVersion}
                                    </Typography>
                                )}
                            </Box>
                        )
                    )}
                    {disclaimer && (
                        <Typography level="bodySmallItalic" sx={{ color: '#666', mt: '8px' }}>
                            {disclaimer}
                        </Typography>
                    )}
                </Box>
            </>
        );
    }

    if (state === 'expired') {
        return (
            <HeroLeadMessage
                icon={<SensorsRounded />}
                title={t('Monitoring unavailable')}
                sub={isSystem ? t('The IoT contract for this system has expired.') : t('The IoT contract for this asset has expired.')}
                iconBox={iconBox}
                headline={headline}
            />
        );
    }

    if (state === 'valid') {
        const sub = isAdmin
            ? networkScanBlocked
                ? t('The contract is active, but a passed network scan is required before the IoT connection can be set up.')
                : t('The contract is active — set up the IoT connection to start receiving data.')
            : isSystem
              ? t('An administrator can set up the IoT connection for this system.')
              : t('An administrator can set up the IoT connection for this asset.');
        return <HeroLeadMessage icon={<LinkOffRounded />} title={t('Not connected')} sub={sub} iconBox={iconBox} headline={headline} />;
    }

    // nocontract without a live device — users get a passive message pointing them to an admin.
    if (!isAdmin) {
        return (
            <HeroLeadMessage
                icon={<LinkOffRounded />}
                title={t('Not connected')}
                sub={isSystem ? t('Contact your administrator to set up an IoT contract for this system.') : t('Contact your administrator to set up an IoT contract for this asset.')}
                iconBox={iconBox}
                headline={headline}
            />
        );
    }
    // nocontract without a live device — admin promo lead (the "Add contract" CTA lives in the admin bar above).
    return (
        <>
            <Box sx={{ display: 'flex', width: iconBox, height: iconBox, alignItems: 'center', justifyContent: 'center', '& svg': { fontSize: compact ? 32 : 40, color: 'primary.main' } }}>
                <TrendingUpRounded />
            </Box>
            <Box>
                <Typography level="titleSmallLight" sx={{ color: '#666' }}>
                    {t('IoT Connection')}
                </Typography>
                <Typography level={headline} sx={{ color: '#333' }}>
                    {t('Not connected')}
                </Typography>
                <Typography level="bodySmall" sx={{ color: '#555', mt: '4px' }}>
                    {isSystem
                        ? t('Bring this system online to unlock live monitoring across all its pumps.')
                        : t('Connect to IoT to unlock live monitoring and smarter, predictive maintenance.')}
                </Typography>
            </Box>
        </>
    );
};

export default HeroLead;
